import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { Camera, History, ShieldAlert, Trash2, Wrench, X } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { api, ApiError, errorMessage } from '../lib/api';
import { formatDate, formatMoney, formatUtcDate, personName, toDateInput } from '../lib/format';
import { useDebouncedValue } from '../lib/hooks';
import {
  INCIDENT_STATUS_LABEL,
  INCIDENT_STATUS_TONE,
  INCIDENT_TYPE_LABEL,
  INVOICE_STATUS_LABEL,
  INVOICE_STATUS_TONE,
  MAX_PHOTOS,
  REPAIR_KIND_LABEL,
  REPAIR_KIND_TONE,
  uploadIncidentPhoto,
  type DamageInvoiceStatus,
  type DeviceHistory,
  type DeviceRepair,
  type DeviceRepairKind,
  type IncidentContext,
  type IncidentDetail,
  type IncidentStatus,
  type IncidentSummary,
  type IncidentType,
} from '../lib/incidents';
import type { AssetListItem, Page, PersonSummary } from '../lib/types';
import { loadImage, renderCrop } from './image-crop';
import { Combobox, PersonPicker } from './pickers';
import { useToast } from './toast';
import { Badge, Button, Card, CardHeader, ConfirmDialog, EmptyState, Field, Input, Modal, Select, Spinner, Textarea } from './ui';

const PHOTO_MAX_SIDE = 2400;
const PHOTO_MAX_BYTES = 4 * 1024 * 1024;

export const IncidentStatusBadge = ({ status }: { status: IncidentStatus }) => <Badge tone={INCIDENT_STATUS_TONE[status]}>{INCIDENT_STATUS_LABEL[status]}</Badge>;

export const InvoiceStatusBadge = ({ status }: { status: DamageInvoiceStatus }) => <Badge tone={INVOICE_STATUS_TONE[status]}>{INVOICE_STATUS_LABEL[status]}</Badge>;

/** Everything that shows incidents, repairs, or invoices. */
export function invalidateIncidents(queryClient: QueryClient) {
  for (const key of ['device-history', 'incidents', 'incident', 'invoices', 'invoice', 'asset', 'work-order']) {
    void queryClient.invalidateQueries({ queryKey: [key] });
  }
}

/** Shrinks a photo in the browser to at most 2,400 pixels on its longest side as a JPEG, which also drops where it was taken. */
export async function preparePhoto(file: File) {
  const source = await loadImage(file);
  try {
    const { naturalWidth: width, naturalHeight: height } = source.image;
    const longest = Math.max(width, height);
    const maxWidth = longest > PHOTO_MAX_SIDE ? Math.round((PHOTO_MAX_SIDE * width) / longest) : width;
    const blob = await renderCrop(source.image, { x: 0, y: 0, width, height }, maxWidth, PHOTO_MAX_BYTES);
    return { blob, fileName: `${file.name.replace(/\.[^.]+$/, '') || 'photo'}.jpg` };
  } finally {
    URL.revokeObjectURL(source.url);
  }
}

/** Why a photo didn't upload, in words someone can act on. */
export const photoProblem = (err: unknown) => (err instanceof ApiError ? err.message : 'This photo couldn’t be opened. Try a JPEG or PNG.');

/** A datetime-local value for a moment, in the browser's time zone. */
export function toLocalInput(value: string | Date) {
  const date = new Date(value);
  return new Date(date.getTime() - date.getTimezoneOffset() * 60_000).toISOString().slice(0, 16);
}

type Entry = { key: string; at: number } & ({ kind: 'incident'; incident: IncidentSummary } | { kind: 'repair'; repair: DeviceRepair });

/** A device's incidents and repairs, newest first. On a ticket it's compact, with a link to the rest on the device. */
export function DeviceHistoryCard({
  asset,
  workOrder,
  compact = false,
}: {
  asset: { id: string; assetTag: string; name: string };
  /** The ticket it's shown on: an incident recorded here is linked to it. */
  workOrder?: { id: string; number: number };
  compact?: boolean;
}) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [reporting, setReporting] = useState(false);
  const [logging, setLogging] = useState(false);
  const [removing, setRemoving] = useState<DeviceRepair | null>(null);
  const query = useQuery({ queryKey: ['device-history', asset.id], queryFn: () => api.get<DeviceHistory>(`/assets/${asset.id}/device-history`) });

  const remove = useMutation({
    mutationFn: (repair: DeviceRepair) => api.delete(`/device-repairs/${repair.id}`),
    onSuccess: () => {
      invalidateIncidents(queryClient);
      setRemoving(null);
      toast.success('Repair record removed');
    },
    onError: (err) => toast.error(errorMessage(err)),
  });

  const entries = useMemo<Entry[]>(() => {
    if (!query.data) return [];
    return [
      ...query.data.incidents.map((incident) => ({ key: `incident-${incident.id}`, at: Date.parse(incident.occurredAt), kind: 'incident' as const, incident })),
      ...query.data.repairs.map((repair) => ({ key: `repair-${repair.id}`, at: Date.parse(`${repair.performedOn}T12:00:00Z`), kind: 'repair' as const, repair })),
    ].sort((a, b) => b.at - a.at);
  }, [query.data]);
  const visible = compact ? entries.slice(0, 5) : entries;
  const totals = query.data?.totals;
  const summary =
    totals &&
    [
      `${totals.incidents} ${totals.incidents === 1 ? 'incident' : 'incidents'}${totals.activeIncidents ? ` (${totals.activeIncidents} not closed)` : ''}`,
      `${totals.repairs} ${totals.repairs === 1 ? 'repair' : 'repairs'}`,
      totals.repairCost > 0 && `${formatMoney(totals.repairCost)} in repairs`,
      totals.billed > 0 && `${formatMoney(totals.billed)} billed`,
    ]
      .filter(Boolean)
      .join(' · ');

  return (
    <Card>
      <CardHeader
        title="Incidents & repairs"
        description={compact ? undefined : 'Stays with this device for its whole life'}
        actions={
          !compact && (
            <div className="flex flex-wrap gap-2">
              <Button size="sm" variant="ghost" icon={<Wrench />} onClick={() => setLogging(true)}>
                Log repair
              </Button>
              <Button size="sm" icon={<ShieldAlert />} onClick={() => setReporting(true)}>
                Report incident
              </Button>
            </div>
          )
        }
      />
      {query.isLoading ? (
        <div className="flex justify-center p-6">
          <Spinner />
        </div>
      ) : query.error ? (
        <p className="px-5 py-4 text-sm text-red-600">{errorMessage(query.error)}</p>
      ) : entries.length === 0 ? (
        <EmptyState
          icon={<History />}
          title="No incidents or repairs"
          description={compact ? undefined : 'Report damage, loss, or theft, and log repairs, to build this device’s history.'}
        />
      ) : (
        <>
          {summary && <p className="border-b border-slate-100 px-5 py-2.5 text-xs text-slate-500">{summary}</p>}
          <ol className="my-4 mr-5 ml-7 space-y-5 border-l border-slate-200">
            {visible.map((entry) =>
              entry.kind === 'incident' ? (
                <IncidentEntry key={entry.key} incident={entry.incident} compact={compact} />
              ) : (
                <li key={entry.key} className="relative pl-5">
                  <span className="absolute top-1.5 -left-[5.5px] size-2.5 rounded-full bg-brand-500 ring-4 ring-white" />
                  <div className="flex flex-wrap items-center gap-1.5">
                    <Badge tone={REPAIR_KIND_TONE[entry.repair.kind]}>{REPAIR_KIND_LABEL[entry.repair.kind]}</Badge>
                    <span className="text-xs text-slate-500">{formatUtcDate(entry.repair.performedOn)}</span>
                    {!compact && entry.repair.canEdit && (
                      <button
                        type="button"
                        onClick={() => setRemoving(entry.repair)}
                        className="ml-auto rounded-md p-1 text-slate-400 hover:bg-slate-100 hover:text-red-600"
                        aria-label={`Remove “${entry.repair.summary}”`}
                        title="Remove record"
                      >
                        <Trash2 className="size-4" />
                      </button>
                    )}
                  </div>
                  <p className="mt-1 text-sm font-medium text-slate-900">{entry.repair.summary}</p>
                  {!compact && entry.repair.details && <p className="mt-0.5 text-sm whitespace-pre-line text-slate-600">{entry.repair.details}</p>}
                  <p className="mt-1 text-xs text-slate-500">
                    {[entry.repair.cost !== null && formatMoney(entry.repair.cost), entry.repair.vendor, entry.repair.createdByName].filter(Boolean).join(' · ')}
                    {entry.repair.workOrder && (
                      <>
                        {' · '}
                        <Link to={`/work-orders/${entry.repair.workOrder.id}`} className="text-brand-700 hover:underline">
                          Work order #{entry.repair.workOrder.number}
                        </Link>
                      </>
                    )}
                    {entry.repair.incident && (
                      <>
                        {' · '}
                        <Link to={`/incidents/${entry.repair.incident.id}`} className="text-brand-700 hover:underline">
                          Incident #{entry.repair.incident.number}
                        </Link>
                      </>
                    )}
                  </p>
                </li>
              ),
            )}
          </ol>
          {compact && entries.length > visible.length && (
            <Link to={`/assets/${asset.id}`} className="block border-t border-slate-100 px-5 py-2.5 text-sm font-medium text-brand-700 hover:bg-slate-50">
              See all {entries.length} on the device
            </Link>
          )}
        </>
      )}
      {compact && (
        <div className="flex flex-wrap gap-2 border-t border-slate-100 px-5 py-3">
          <Button size="sm" className="flex-1" icon={<ShieldAlert />} onClick={() => setReporting(true)}>
            Record incident
          </Button>
          <Button size="sm" variant="ghost" icon={<Wrench />} onClick={() => setLogging(true)}>
            Log repair
          </Button>
        </div>
      )}

      <ReportIncidentModal open={reporting} onClose={() => setReporting(false)} asset={asset} workOrderId={workOrder?.id} stay={!!workOrder} />
      <LogRepairModal
        open={logging}
        onClose={() => setLogging(false)}
        assetId={asset.id}
        incidents={query.data?.incidents.map(({ id, number, type }) => ({ id, number, type })) ?? []}
      />
      <ConfirmDialog
        open={!!removing}
        onClose={() => setRemoving(null)}
        onConfirm={() => removing && remove.mutate(removing)}
        title="Remove this repair record?"
        description={removing ? `“${removing.summary}” comes out of the device’s history.` : ''}
        confirmLabel="Remove"
        loading={remove.isPending}
      />
    </Card>
  );
}

function IncidentEntry({ incident, compact }: { incident: IncidentSummary; compact: boolean }) {
  const details = [
    incident.responsiblePerson && personName(incident.responsiblePerson),
    incident.photoCount > 0 && `${incident.photoCount} ${incident.photoCount === 1 ? 'photo' : 'photos'}`,
  ].filter(Boolean);
  return (
    <li className="relative pl-5">
      <span className="absolute top-1.5 -left-[5.5px] size-2.5 rounded-full bg-amber-500 ring-4 ring-white" />
      <div className="flex flex-wrap items-center gap-1.5">
        <Badge tone="amber">{INCIDENT_TYPE_LABEL[incident.type]}</Badge>
        <IncidentStatusBadge status={incident.status} />
        <span className="text-xs text-slate-500">{formatDate(incident.occurredAt)}</span>
      </div>
      <Link to={`/incidents/${incident.id}`} className="mt-1 block text-sm font-medium text-slate-900 hover:text-brand-700">
        Incident #{incident.number}
      </Link>
      {!compact && <p className="mt-0.5 line-clamp-2 text-sm text-slate-600">{incident.description}</p>}
      {details.length > 0 && <p className="mt-1 text-xs text-slate-500">{details.join(' · ')}</p>}
      {incident.invoices.length > 0 && (
        <div className="mt-1.5 flex flex-wrap gap-1.5">
          {incident.invoices.map((invoice) => (
            <Link
              key={invoice.id}
              to={`/invoices/${invoice.id}`}
              className="inline-flex items-center gap-1.5 rounded-md border border-slate-200 px-1.5 py-0.5 text-xs text-slate-700 hover:bg-slate-50"
            >
              Invoice #{invoice.number} · {formatMoney(invoice.total)} <InvoiceStatusBadge status={invoice.status} />
            </Link>
          ))}
        </div>
      )}
    </li>
  );
}

interface PendingPhoto {
  key: string;
  name: string;
  preview: string;
  id: string | null;
  error: string | null;
}

/** Records damage, loss, or theft on a device, with photos and who had it. */
export function ReportIncidentModal({
  open,
  onClose,
  asset,
  workOrderId,
  stay = false,
}: {
  open: boolean;
  onClose(): void;
  /** Leave it out to choose the device in the form. */
  asset?: { id: string; assetTag: string; name: string } | null;
  workOrderId?: string;
  /** Stay on the current page afterwards, rather than opening the incident. */
  stay?: boolean;
}) {
  const toast = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [device, setDevice] = useState(asset ?? null);
  const [deviceQuery, setDeviceQuery] = useState('');
  const [type, setType] = useState<IncidentType>('ACCIDENTAL_DAMAGE');
  const [occurredAt, setOccurredAt] = useState(() => toLocalInput(new Date()));
  const [description, setDescription] = useState('');
  const [person, setPerson] = useState<PersonSummary | null>(null);
  const [personChosen, setPersonChosen] = useState(false);
  const [photos, setPhotos] = useState<PendingPhoto[]>([]);
  const photosRef = useRef(photos);

  useEffect(() => {
    photosRef.current = photos;
  });
  useEffect(() => () => photosRef.current.forEach((photo) => URL.revokeObjectURL(photo.preview)), []);

  // A fresh form each time it opens.
  useEffect(() => {
    if (!open) return;
    setDevice(asset ?? null);
    setDeviceQuery('');
    setType('ACCIDENTAL_DAMAGE');
    setOccurredAt(toLocalInput(new Date()));
    setDescription('');
    setPerson(null);
    setPersonChosen(false);
    setPhotos((current) => {
      current.forEach((photo) => URL.revokeObjectURL(photo.preview));
      return [];
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, asset?.id]);

  const occurredIso = occurredAt && !Number.isNaN(Date.parse(occurredAt)) ? new Date(occurredAt).toISOString() : null;
  const context = useQuery({
    queryKey: ['incident-context', device?.id, occurredIso],
    queryFn: () => api.get<IncidentContext>('/incidents/context', { assetId: device!.id, occurredAt: occurredIso! }),
    enabled: open && !!device && !!occurredIso,
  });
  useEffect(() => {
    if (!personChosen && context.data) setPerson(context.data.suggestedPerson);
  }, [context.data, personChosen]);

  const deviceSearch = useDebouncedValue(deviceQuery.trim(), 250);
  const devices = useQuery({
    queryKey: ['assets', 'incident-picker', deviceSearch],
    queryFn: () => api.get<Page<AssetListItem>>('/assets', { search: deviceSearch, pageSize: 8 }),
    enabled: open && !device && deviceSearch.length > 0,
  });

  const addPhotos = (files: File[]) => {
    for (const file of files.slice(0, Math.max(0, MAX_PHOTOS - photos.length))) {
      const key = `${file.name}-${file.size}-${Math.random().toString(36).slice(2)}`;
      setPhotos((current) => [...current, { key, name: file.name, preview: URL.createObjectURL(file), id: null, error: null }]);
      void (async () => {
        try {
          const prepared = await preparePhoto(file);
          const saved = await uploadIncidentPhoto(prepared.blob, prepared.fileName);
          setPhotos((current) => current.map((photo) => (photo.key === key ? { ...photo, id: saved.id } : photo)));
        } catch (err) {
          setPhotos((current) => current.map((photo) => (photo.key === key ? { ...photo, error: photoProblem(err) } : photo)));
        }
      })();
    }
  };

  const removePhoto = (key: string) =>
    setPhotos((current) =>
      current.filter((photo) => {
        if (photo.key === key) URL.revokeObjectURL(photo.preview);
        return photo.key !== key;
      }),
    );

  const uploading = photos.some((photo) => !photo.id && !photo.error);
  const create = useMutation({
    mutationFn: () =>
      api.post<IncidentDetail>('/incidents', {
        assetId: device!.id,
        type,
        occurredAt: occurredIso,
        description,
        responsiblePersonId: person?.id ?? null,
        workOrderId: workOrderId ?? null,
        photoIds: photos.flatMap((photo) => (photo.id ? [photo.id] : [])),
      }),
    onSuccess: (incident) => {
      invalidateIncidents(queryClient);
      toast.success(`Recorded incident #${incident.number}`);
      onClose();
      if (!stay) navigate(`/incidents/${incident.id}`);
    },
    onError: (err) => toast.error(errorMessage(err)),
  });

  const personHint =
    !personChosen && context.data?.suggestion === 'CHECKED_OUT'
      ? 'It was checked out to them at the time'
      : !personChosen && context.data?.suggestion === 'LAST_CHECKOUT'
        ? 'The last person it was checked out to'
        : 'A damage invoice bills this person’s family';

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="lg"
      title="Report an incident"
      description="Damage, loss, or theft. It stays in the device’s history for its whole life."
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button type="submit" form="report-incident" variant="primary" loading={create.isPending} disabled={!device || !occurredIso || uploading}>
            {uploading ? 'Uploading photos…' : 'Record incident'}
          </Button>
        </>
      }
    >
      <form
        id="report-incident"
        className="space-y-4"
        onSubmit={(event) => {
          event.preventDefault();
          create.mutate();
        }}
      >
        {!asset && (
          <Field as="div" label="Device" required>
            {device ? (
              <div className="flex items-center justify-between gap-3 rounded-lg border border-slate-200 px-3 py-2 text-sm">
                <span className="min-w-0 truncate">
                  <span className="font-mono font-semibold text-slate-900">{device.assetTag}</span> · {device.name}
                </span>
                <Button size="sm" variant="ghost" icon={<X />} onClick={() => setDevice(null)} aria-label="Choose another device" />
              </div>
            ) : (
              <Combobox
                inline
                query={deviceQuery}
                onQueryChange={setDeviceQuery}
                loading={devices.isFetching}
                items={deviceSearch ? (devices.data?.items ?? []) : []}
                getKey={(item) => item.id}
                placeholder="Search by asset tag, name, or serial number"
                emptyText="No devices match"
                onSelect={(item) => setDevice({ id: item.id, assetTag: item.assetTag, name: item.name })}
                renderItem={(item) => (
                  <div className="min-w-0">
                    <p className="font-mono text-xs font-semibold text-slate-900">{item.assetTag}</p>
                    <p className="truncate text-xs text-slate-500">{item.name}</p>
                  </div>
                )}
              />
            )}
          </Field>
        )}
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="What happened" required>
            <Select value={type} onChange={(event) => setType(event.target.value as IncidentType)}>
              {(Object.keys(INCIDENT_TYPE_LABEL) as IncidentType[]).map((value) => (
                <option key={value} value={value}>
                  {INCIDENT_TYPE_LABEL[value]}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="When" required>
            <Input type="datetime-local" value={occurredAt} max={toLocalInput(new Date())} onChange={(event) => setOccurredAt(event.target.value)} required />
          </Field>
        </div>
        <Field as="div" label="Who had the device" hint={personHint}>
          <PersonPicker
            value={person}
            onChange={(next) => {
              setPerson(next);
              setPersonChosen(true);
            }}
            placeholder="Search for the student or staff member"
          />
        </Field>
        <Field label="Description" required>
          <Textarea
            rows={4}
            value={description}
            onChange={(event) => setDescription(event.target.value)}
            placeholder="What happened, and the damage you can see"
            minLength={3}
            maxLength={5000}
            required
          />
        </Field>
        <Field label="Photos" hint={`Up to ${MAX_PHOTOS}. They’re resized before they upload.`}>
          <div className="grid grid-cols-3 gap-2 sm:grid-cols-5">
            {photos.map((photo) => (
              <div key={photo.key} className="relative aspect-square overflow-hidden rounded-lg border border-slate-200 bg-slate-100">
                <img src={photo.preview} alt={photo.name} className="size-full object-cover" />
                {!photo.id && !photo.error && (
                  <div className="absolute inset-0 flex items-center justify-center bg-white/60">
                    <Spinner />
                  </div>
                )}
                {photo.error && (
                  <div className="absolute inset-0 flex items-center justify-center bg-red-50/95 p-1.5 text-center text-[11px] leading-tight text-red-700">{photo.error}</div>
                )}
                <button
                  type="button"
                  onClick={() => removePhoto(photo.key)}
                  className="absolute top-1 right-1 rounded-full bg-slate-900/70 p-0.5 text-white hover:bg-slate-900"
                  aria-label={`Remove ${photo.name}`}
                >
                  <X className="size-3.5" />
                </button>
              </div>
            ))}
            {photos.length < MAX_PHOTOS && (
              <label className="flex aspect-square cursor-pointer flex-col items-center justify-center gap-1 rounded-lg border-2 border-dashed border-slate-300 text-xs text-slate-500 hover:border-brand-400 hover:text-brand-700">
                <Camera className="size-5" />
                Add photos
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  className="sr-only"
                  onChange={(event) => {
                    const files = [...(event.target.files ?? [])];
                    event.target.value = '';
                    addPhotos(files);
                  }}
                />
              </label>
            )}
          </div>
        </Field>
      </form>
    </Modal>
  );
}

/** Adds work done on a device to its history. */
export function LogRepairModal({
  open,
  onClose,
  assetId,
  incidentId,
  incidents = [],
}: {
  open: boolean;
  onClose(): void;
  assetId: string;
  /** The incident it's for, fixed. */
  incidentId?: string;
  /** Incidents to choose from, when it isn't fixed. */
  incidents?: { id: string; number: number; type: IncidentType }[];
}) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const blank = () => ({ kind: 'REPAIR' as DeviceRepairKind, performedOn: toDateInput(new Date()), summary: '', details: '', cost: '', vendor: '', incidentId: incidentId ?? '' });
  const [form, setForm] = useState(blank);

  useEffect(() => {
    if (open) setForm(blank());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const save = useMutation({
    mutationFn: () =>
      api.post<DeviceRepair>(`/assets/${assetId}/repairs`, {
        kind: form.kind,
        performedOn: form.performedOn,
        summary: form.summary,
        details: form.details,
        cost: form.cost === '' ? null : Number(form.cost),
        vendor: form.vendor,
        incidentId: form.incidentId || null,
      }),
    onSuccess: () => {
      invalidateIncidents(queryClient);
      toast.success('Repair logged');
      onClose();
    },
    onError: (err) => toast.error(errorMessage(err)),
  });

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Log a repair"
      description="Work done on this device. It stays in the device’s history."
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button type="submit" form="log-repair" variant="primary" loading={save.isPending}>
            Log repair
          </Button>
        </>
      }
    >
      <form
        id="log-repair"
        className="space-y-4"
        onSubmit={(event) => {
          event.preventDefault();
          save.mutate();
        }}
      >
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Kind" required>
            <Select value={form.kind} onChange={(event) => setForm({ ...form, kind: event.target.value as DeviceRepairKind })}>
              {(Object.keys(REPAIR_KIND_LABEL) as DeviceRepairKind[]).map((kind) => (
                <option key={kind} value={kind}>
                  {REPAIR_KIND_LABEL[kind]}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Date" required>
            <Input type="date" value={form.performedOn} max={toDateInput(new Date())} onChange={(event) => setForm({ ...form, performedOn: event.target.value })} required />
          </Field>
        </div>
        <Field label="What was done" required>
          <Input value={form.summary} onChange={(event) => setForm({ ...form, summary: event.target.value })} placeholder="e.g. Replaced the screen" maxLength={200} required />
        </Field>
        <Field label="Details">
          <Textarea rows={3} value={form.details} onChange={(event) => setForm({ ...form, details: event.target.value })} maxLength={5000} />
        </Field>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Cost" hint="What the repair cost the school">
            <Input type="number" min={0} step="0.01" value={form.cost} onChange={(event) => setForm({ ...form, cost: event.target.value })} placeholder="0.00" />
          </Field>
          <Field label="Vendor">
            <Input value={form.vendor} onChange={(event) => setForm({ ...form, vendor: event.target.value })} placeholder="Who did the work, if not your team" maxLength={120} />
          </Field>
        </div>
        {!incidentId && incidents.length > 0 && (
          <Field label="Incident" hint="The incident this repair was for, if any">
            <Select value={form.incidentId} onChange={(event) => setForm({ ...form, incidentId: event.target.value })}>
              <option value="">None</option>
              {incidents.map((incident) => (
                <option key={incident.id} value={incident.id}>
                  #{incident.number} · {INCIDENT_TYPE_LABEL[incident.type]}
                </option>
              ))}
            </Select>
          </Field>
        )}
      </form>
    </Modal>
  );
}
